package ui.theme

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*

// Import ThemeMode from commonMain
import ui.theme.ThemeMode

// Theme Configuration
data class AppTheme(
    val colors: ColorScheme,
    val typography: TypographyScheme,
    val shapes: ShapeScheme,
    val shadows: ShadowScheme,
    val mode: ThemeMode = ThemeMode.LIGHT
)

data class ColorScheme(
    val primary: String,
    val primaryVariant: String,
    val secondary: String,
    val background: String,
    val surface: String,
    val surfaceVariant: String,
    val onPrimary: String,
    val onSecondary: String,
    val onBackground: String,
    val onSurface: String,
    val onSurfaceVariant: String,
    val error: String,
    val glassBackground: String,
    val glassBackgroundHover: String,
    val glassBorder: String,
    val glassHighlight: String,
    val primaryGradient: String,
    val backgroundGradient: String
)

data class TypographyScheme(
    val fontFamily: String,
    val h1: TextStyle,
    val h2: TextStyle,
    val h3: TextStyle,
    val body1: TextStyle,
    val body2: TextStyle,
    val caption: TextStyle
)

data class TextStyle(
    val fontSize: String,
    val fontWeight: Int,
    val lineHeight: Double,
    val letterSpacing: String = "0em"
)

data class ShapeScheme(
    val small: CSSLengthValue,
    val medium: CSSLengthValue,
    val large: CSSLengthValue,
    val card: CSSLengthValue,
    val button: CSSLengthValue,
    val modal: CSSLengthValue
)

data class ShadowScheme(
    val card: String,
    val cardHover: String,
    val button: String,
    val buttonHover: String,
    val glass: String,
    val glassHover: String
)

// Light Theme
val lightTheme = AppTheme(
    colors = ColorScheme(
        primary = AppColors.primary.toString(),
        primaryVariant = AppColors.primaryVariant.toString(),
        secondary = AppColors.secondary.toString(),
        background = AppColors.background.toString(),
        surface = AppColors.surface.toString(),
        surfaceVariant = AppColors.surfaceVariant.toString(),
        onPrimary = AppColors.onPrimary.toString(),
        onSecondary = AppColors.onSecondary.toString(),
        onBackground = AppColors.onBackground.toString(),
        onSurface = AppColors.onSurface.toString(),
        onSurfaceVariant = AppColors.onSurfaceVariant.toString(),
        error = AppColors.error.toString(),
        glassBackground = AppColors.glassBackground,
        glassBackgroundHover = AppColors.glassBackgroundHover,
        glassBorder = AppColors.glassBorder,
        glassHighlight = AppColors.glassHighlight,
        primaryGradient = AppColors.primaryGradient,
        backgroundGradient = AppColors.backgroundGradient
    ),
    typography = TypographyScheme(
        fontFamily = AppTypography.primaryFont,
        h1 = TextStyle(AppTypography.xl6, AppTypography.extraBold, AppTypography.leadingTight, AppTypography.trackingTight),
        h2 = TextStyle(AppTypography.xl4, AppTypography.bold, AppTypography.leadingTight, AppTypography.trackingTight),
        h3 = TextStyle(AppTypography.xl2, AppTypography.semiBold, AppTypography.leadingSnug),
        body1 = TextStyle(AppTypography.base, AppTypography.regular, AppTypography.leadingNormal),
        body2 = TextStyle(AppTypography.sm, AppTypography.regular, AppTypography.leadingNormal),
        caption = TextStyle(AppTypography.xs, AppTypography.medium, AppTypography.leadingNormal, AppTypography.trackingWide)
    ),
    shapes = ShapeScheme(
        small = AppShapes.lg,
        medium = AppShapes.xl,
        large = AppShapes.xl2,
        card = AppShapes.cardRadius,
        button = AppShapes.buttonRadius,
        modal = AppShapes.modalRadius
    ),
    shadows = ShadowScheme(
        card = AppShadows.glass,
        cardHover = AppShadows.glassHover,
        button = AppShadows.md,
        buttonHover = AppShadows.lg,
        glass = AppShadows.glass,
        glassHover = AppShadows.glassHover
    ),
    mode = ThemeMode.LIGHT
)

// Dark Theme
val darkTheme = AppTheme(
    colors = ColorScheme(
        primary = DarkColors.primary.toString(),
        primaryVariant = DarkColors.primaryVariant.toString(),
        secondary = DarkColors.secondary.toString(),
        background = DarkColors.background.toString(),
        surface = DarkColors.surface.toString(),
        surfaceVariant = DarkColors.surfaceVariant.toString(),
        onPrimary = DarkColors.onPrimary.toString(),
        onSecondary = DarkColors.onSecondary.toString(),
        onBackground = DarkColors.onBackground.toString(),
        onSurface = DarkColors.onSurface.toString(),
        onSurfaceVariant = DarkColors.onSurfaceVariant.toString(),
        error = DarkColors.error.toString(),
        glassBackground = DarkColors.glassBackground,
        glassBackgroundHover = DarkColors.glassBackgroundHover,
        glassBorder = DarkColors.glassBorder,
        glassHighlight = DarkColors.glassHighlight,
        primaryGradient = DarkColors.primaryGradient,
        backgroundGradient = DarkColors.backgroundGradient
    ),
    typography = TypographyScheme(
        fontFamily = AppTypography.primaryFont,
        h1 = TextStyle(AppTypography.xl6, AppTypography.extraBold, AppTypography.leadingTight, AppTypography.trackingTight),
        h2 = TextStyle(AppTypography.xl4, AppTypography.bold, AppTypography.leadingTight, AppTypography.trackingTight),
        h3 = TextStyle(AppTypography.xl2, AppTypography.semiBold, AppTypography.leadingSnug),
        body1 = TextStyle(AppTypography.base, AppTypography.regular, AppTypography.leadingNormal),
        body2 = TextStyle(AppTypography.sm, AppTypography.regular, AppTypography.leadingNormal),
        caption = TextStyle(AppTypography.xs, AppTypography.medium, AppTypography.leadingNormal, AppTypography.trackingWide)
    ),
    shapes = ShapeScheme(
        small = AppShapes.lg,
        medium = AppShapes.xl,
        large = AppShapes.xl2,
        card = AppShapes.cardRadius,
        button = AppShapes.buttonRadius,
        modal = AppShapes.modalRadius
    ),
    shadows = ShadowScheme(
        card = AppShadows.glass,
        cardHover = AppShadows.glassHover,
        button = AppShadows.md,
        buttonHover = AppShadows.lg,
        glass = AppShadows.glass,
        glassHover = AppShadows.glassHover
    ),
    mode = ThemeMode.DARK
)

// Theme Context
val ThemeContext = compositionLocalOf { lightTheme }

@Composable
fun AppThemeProvider(
    theme: AppTheme = lightTheme,
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(ThemeContext provides theme) {
        content()
    }
}

// Theme Access Helper
@Composable
fun theme(): AppTheme = ThemeContext.current
